/**
 * Utility functions for handling question jump logic.
 *
 * Jump logic allows questions to conditionally skip other questions or end the questionnaire early.
 *
 * - jump_target = -1: Stop rendering questions (end of questionnaire)
 * - jump_target = N: Skip to question with display_order = N
 * - answer_options: Each option can have a jump target (conditional jumps for
 *   single-select and boolean questions)
 */

/** Stable answer_options keys for boolean Yes/No branching (same shape as single-select). */
export const BOOLEAN_ANSWER_OPTION_YES = "Yes";
export const BOOLEAN_ANSWER_OPTION_NO = "No";

export const DEFAULT_BOOLEAN_ANSWER_OPTIONS: Record<string, number | null> = {
  [BOOLEAN_ANSWER_OPTION_YES]: null,
  [BOOLEAN_ANSWER_OPTION_NO]: null,
};

export const BOOLEAN_ANSWER_OPTION_KEYS_ORDER = [
  BOOLEAN_ANSWER_OPTION_YES,
  BOOLEAN_ANSWER_OPTION_NO,
] as const;

/** Map a stored boolean answer to the answer_options key used for jump targets. */
export function booleanAnswerToOptionKey(answer: unknown): string | null {
  if (
    answer === true ||
    answer === "true" ||
    answer === BOOLEAN_ANSWER_OPTION_YES
  ) {
    return BOOLEAN_ANSWER_OPTION_YES;
  }
  if (
    answer === false ||
    answer === "false" ||
    answer === BOOLEAN_ANSWER_OPTION_NO
  ) {
    return BOOLEAN_ANSWER_OPTION_NO;
  }
  return null;
}

/** Ensure Yes/No keys exist for authoring / jump lookup (preserves configured targets). */
export function normalizeBooleanAnswerOptions(
  opts: Record<string, number | null> | null | undefined,
): Record<string, number | null> {
  return {
    [BOOLEAN_ANSWER_OPTION_YES]:
      opts?.[BOOLEAN_ANSWER_OPTION_YES] ?? opts?.true ?? null,
    [BOOLEAN_ANSWER_OPTION_NO]:
      opts?.[BOOLEAN_ANSWER_OPTION_NO] ?? opts?.false ?? null,
  };
}

export interface QuestionWithJump {
  id: string;
  display_order: number;
  jump_target?: number | null;
  answer_options?: Record<string, number | null> | null;
  answer_type: string;
  [key: string]: unknown;
}

function hasAnswerValue(answer: unknown): boolean {
  return answer !== undefined && answer !== null && answer !== "";
}

function supportsConditionalOptionJumps(answerType: string): boolean {
  return (
    answerType === "single_select" ||
    answerType === "branch_on_choice" ||
    answerType === "boolean"
  );
}

/** Resolve per-option jump target from answer_options for the given answer. */
export function getOptionJumpTarget(
  question: Pick<QuestionWithJump, "answer_type" | "answer_options">,
  answer: unknown,
): number | null {
  if (
    !question.answer_options ||
    !supportsConditionalOptionJumps(question.answer_type)
  ) {
    return null;
  }
  if (!hasAnswerValue(answer)) {
    return null;
  }

  let optionKey: string | null = null;
  if (question.answer_type === "boolean") {
    optionKey = booleanAnswerToOptionKey(answer);
  } else if (typeof answer === "string") {
    optionKey = answer;
  }

  if (optionKey == null) {
    return null;
  }

  const jumpTarget = question.answer_options[optionKey];
  if (jumpTarget === null || jumpTarget === undefined) {
    return null;
  }
  return jumpTarget;
}

/**
 * Filter questions based on unconditional jump_target logic.
 *
 * This handles jump_target at the question level (not answer-specific).
 * If a question has jump_target = -1, all subsequent questions are excluded.
 * If a question has jump_target = N, all questions with display_order < N are excluded.
 *
 * Note: This does NOT handle conditional jumps from answer_options - those must be
 * handled at runtime based on user answers.
 *
 * @param questions - Array of questions (should already be sorted by display_order)
 * @returns Filtered array of questions with jump logic applied
 */
export function filterQuestionsWithJumpLogic<T extends QuestionWithJump>(
  questions: T[],
): T[] {
  // Sort questions by display_order to ensure correct processing
  const sortedQuestions = [...questions].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );

  const result: T[] = [];
  let skipUntilOrder: number | null = null;

  for (const question of sortedQuestions) {
    // If we're skipping, check if we've reached the target display_order
    if (skipUntilOrder !== null) {
      if (question.display_order < skipUntilOrder) {
        continue; // Skip this question
      }
      // We've reached or passed the target, reset skip
      skipUntilOrder = null;
    }

    // Add the current question
    result.push(question);

    // Check if this question has a jump_target
    if (question.jump_target === -1) {
      // -1 means end the questionnaire here
      break;
    }

    if (
      question.jump_target !== null &&
      question.jump_target !== undefined &&
      question.jump_target > question.display_order
    ) {
      // Jump to a specific display_order - skip all questions until we reach it
      skipUntilOrder = question.jump_target;
    }
  }

  return result;
}

/**
 * Determine if a question should be shown based on previous answers.
 *
 * This handles conditional jumps from answer_options for single-select and boolean
 * questions. If a previous question had an answer selected with a jump target,
 * this determines if the current question should be skipped.
 *
 * Note: Multi-select questions do not support conditional jumps due to the complexity
 * of handling multiple selected options with potentially conflicting jump targets.
 *
 * @param question - The question to check
 * @param previousAnswers - Map of question_id -> selected answer(s)
 * @param allQuestions - All questions (sorted by display_order)
 * @returns true if the question should be shown, false if it should be skipped
 */
export function shouldShowQuestionBasedOnAnswers<T extends QuestionWithJump>(
  question: T,
  previousAnswers: Record<string, unknown>,
  allQuestions: T[],
): boolean {
  // Find the most recent question before this one that was answered
  const sortedQuestions = [...allQuestions].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );

  let activeJumpTarget: number | null = null;

  for (const prevQ of sortedQuestions) {
    if (prevQ.display_order >= question.display_order) {
      // We've reached the current question, stop checking
      break;
    }

    const answer = previousAnswers[prevQ.id];
    // Use hasAnswerValue so boolean `false` ("No") is treated as answered
    if (!hasAnswerValue(answer)) {
      continue;
    }

    const jumpTarget = getOptionJumpTarget(prevQ, answer);
    if (jumpTarget !== null) {
      if (jumpTarget === -1) {
        // End questionnaire - don't show any more questions
        return false;
      }
      // Set active jump target (most recent jump takes precedence)
      activeJumpTarget = jumpTarget;
    }
    // Note: Multi-select questions do not support conditional jumps
  }

  // If there's an active jump target, check if we should skip this question
  if (activeJumpTarget !== null && question.display_order < activeJumpTarget) {
    return false;
  }

  return true;
}

/**
 * Get the list of questions that should be visible when following both unconditional
 * (jump_target) and conditional (single-select / boolean answer_options) jump logic.
 * Uses the full question list so that questions reached only by conditional jump (e.g. to
 * display_order 200) are included.
 *
 * @param questions - All questions (sorted by display_order)
 * @param answers - Current answers (question_id -> value)
 * @returns Questions that are on the current path
 */
export function getVisibleQuestionsWithJumpLogic<T extends QuestionWithJump>(
  questions: T[],
  answers: Record<string, unknown>,
): T[] {
  const sorted = [...questions].sort(
    (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
  );
  const result: T[] = [];
  let skipUntilOrder: number | null = null;

  for (const question of sorted) {
    if (skipUntilOrder !== null) {
      if (question.display_order < skipUntilOrder) continue;
      skipUntilOrder = null;
    }

    result.push(question);

    const answer = answers[question.id];
    let nextJump: number | null = getOptionJumpTarget(question, answer);
    if (
      nextJump === null &&
      question.jump_target !== null &&
      question.jump_target !== undefined
    ) {
      nextJump = question.jump_target;
    }

    if (nextJump === -1) break;
    if (nextJump !== null && nextJump > question.display_order) {
      skipUntilOrder = nextJump;
    }
  }

  return result;
}
